import React from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Piano,
  Repeat,
  Activity,
  Upload,
  FileText,
  Music,
  Zap,
  ShieldCheck,
  Heart,
  X,
  Plus as PlusIcon,
  Wifi,
  WifiOff,
  ListMusic,
  ChevronRight,
  Archive,
  Hash,
  Maximize2,
  StickyNote,
  Vibrate,
  VibrateOff,
  Timer,
  Mic,
  Camera,
  Video,
  Image as ImageIcon,
  Sparkles,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { Waveform } from "../components/Waveform";
import { ChordsDialog } from "../components/ChordsDialog";
import { UploadDialog } from "../components/UploadDialog";
import { PdfViewer } from "../components/PdfViewer";
import { useMetronome } from "../components/useMetronome";
import { useTempoTicker } from "../components/useTempoTicker";
import { CircularCountdown } from "../components/CircularCountdown";
import { RecordingsDialog } from "../components/RecordingsDialog";
import { useSetlist } from "../components/useSetlist";
import { SetlistDialog } from "../components/SetlistDialog";
import { BackupDialog } from "../components/BackupDialog";
import { NumpadDialog } from "../components/NumpadDialog";
import { GalleryDialog } from "../components/GalleryDialog";
import { KaraokeDialog } from "../components/KaraokeDialog";
import { useAudioDurations, formatDuration, parseDurationInput, resolveSongDuration } from "../lib/audioDurations";
import { PerformanceMode } from "../components/PerformanceMode";
import { TapTempoButton } from "../components/TapTempoButton";
import { CompletenessRing } from "../components/CompletenessRing";
import { VisualBeat } from "../components/VisualBeat";
import { computeCompleteness } from "../components/completeness";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { isOnline } from "../lib/pwa";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (s) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${ss.toString().padStart(2, "0")}`;
};

// BPM display format: 000.000 (3 integer digits, 3 decimals)
const fmtBpm = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const num = parseFloat(n);
  if (isNaN(num)) return "";
  const [intP, decP = ""] = num.toFixed(3).split(".");
  return intP.padStart(3, "0") + "." + decP.padEnd(3, "0");
};

const fileUrl = (kind, name) => {
  if (!name) return null;
  const base = `${API}/files/${kind}/${name}`;
  // Append the auth token as ?t= so <audio> and <iframe> tags work even
  // when the browser blocks cookies (Brave Shields strict, Safari ITP).
  if (typeof window !== "undefined") {
    const t = window.localStorage.getItem("gl26_auth_token");
    if (t) return `${base}?t=${encodeURIComponent(t)}`;
  }
  return base;
};

// ---------- Side feature card ----------
const FeatureCard = ({ icon: Icon, title, subtitle, sub2 }) => (
  <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl p-4 flex items-start gap-3">
    <div className="w-10 h-10 rounded-lg bg-black border border-[var(--gl-gold)]/40 flex items-center justify-center text-[var(--gl-gold)] shrink-0">
      <Icon size={18} />
    </div>
    <div className="min-w-0">
      <div className="text-white font-bold tracking-wide text-sm">{title}</div>
      <div className="text-xs text-neutral-400 leading-snug">{subtitle}</div>
      {sub2 && <div className="text-xs text-neutral-500 leading-snug">{sub2}</div>}
    </div>
  </div>
);

// ---------- New song dialog ----------
const NewSongDialog = ({ open, onOpenChange, onCreated, existingIds }) => {
  const [id, setId] = React.useState("");
  const [title, setTitle] = React.useState("");
  const [composer, setComposer] = React.useState("");
  const [duration, setDuration] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    const cleanId = id.trim().toUpperCase();
    if (!/^A\d{3}$/.test(cleanId)) {
      toast.error("Le code doit être au format A001 → A999");
      return;
    }
    if (existingIds.includes(cleanId)) {
      toast.error(`${cleanId} existe déjà`);
      return;
    }
    if (!title.trim()) {
      toast.error("Titre requis");
      return;
    }
    // Optional duration (mm:ss). Invalid -> error; empty -> null.
    let durationSeconds = null;
    if (duration.trim()) {
      durationSeconds = parseDurationInput(duration);
      if (durationSeconds === null) {
        toast.error("Durée invalide. Format mm:ss (ex: 3:42)");
        return;
      }
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/songs`, {
        id: cleanId,
        title: title.trim(),
        composer: composer.trim(),
        duration_seconds: durationSeconds,
      });
      onCreated?.(data);
      toast.success(`${cleanId} ajouté`);
      setId(""); setTitle(""); setComposer(""); setDuration("");
      onOpenChange(false);
    } catch (e) {
      toast.error("Échec création morceau");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-md" data-testid="new-song-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl text-[var(--gl-gold)] font-display">Ajouter un morceau</DialogTitle>
          <DialogDescription className="text-neutral-400 text-sm">
            Code A001 → A999, titre, et compositeur optionnel.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <label className="text-xs text-neutral-400 uppercase">Code</label>
            <Input
              value={id}
              onChange={(e) => setId(e.target.value.toUpperCase())}
              placeholder="A004"
              className="bg-black border-white/15 h-12 text-lg"
              data-testid="new-song-id"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[var(--gl-gold)] font-bold">Titre du morceau</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Hallelujah"
              className="bg-black border-2 border-[var(--gl-gold)]/50 h-12 text-lg text-[var(--gl-gold)] font-bold focus-visible:border-[var(--gl-gold)] focus-visible:ring-[var(--gl-gold)]"
              data-testid="new-song-title"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[var(--gl-green)] font-bold">Compositeur (optionnel)</label>
            <Input
              value={composer}
              onChange={(e) => setComposer(e.target.value)}
              placeholder="Ex: Leonard Cohen"
              className="bg-black border-2 border-[var(--gl-green)]/50 h-12 text-lg text-[var(--gl-green)] font-bold focus-visible:border-[var(--gl-green)] focus-visible:ring-[var(--gl-green)]"
              data-testid="new-song-artist"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">
              Durée (optionnel · mm:ss)
            </label>
            <Input
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="3:42"
              inputMode="numeric"
              className="bg-black border border-white/15 h-12 text-lg text-white font-bold tabular-nums focus-visible:border-[var(--gl-gold)] focus-visible:ring-[var(--gl-gold)]"
              data-testid="new-song-duration"
            />
          </div>
          <button
            onClick={submit}
            disabled={busy}
            className="w-full tap-lg rounded-xl bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider mt-2 disabled:opacity-50"
            data-testid="new-song-submit"
          >
            {busy ? "Création…" : "Créer le morceau"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Main page ----------
export default function GiGLife() {
  const [songs, setSongs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [selectedId, setSelectedId] = React.useState(null);
  const [showPdf, setShowPdf] = React.useState(false);
  const [showChords, setShowChords] = React.useState(false);
  const [showUpload, setShowUpload] = React.useState(false);
  const [showNewSong, setShowNewSong] = React.useState(false);

  // Auto-open PDF when the current song changes (live performance helper).
  // Persisted in localStorage; the toggle is exposed in the UI.
  const [pdfSync, setPdfSync] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("gl26_pdf_sync") === "1";
  });
  const togglePdfSync = React.useCallback(() => {
    setPdfSync((cur) => {
      const next = !cur;
      try { window.localStorage.setItem("gl26_pdf_sync", next ? "1" : "0"); } catch { /* noop */ }
      return next;
    });
  }, []);
  // Track which song we have auto-opened the PDF for, so we don't re-open it
  // after the user has manually closed it for the same song.
  const lastAutoOpenedRef = React.useRef(null);

  // Audio state
  const audioRef = React.useRef(null);
  const playWhenReadyRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [loop, setLoop] = React.useState(false);

  // Local drafts for inline edits (committed on blur to avoid spamming PATCH on every keystroke)
  const [bpmFocused, setBpmFocused] = React.useState(false);
  const [bpmDraft, setBpmDraft] = React.useState("");
  const [keyFocused, setKeyFocused] = React.useState(false);
  const [keyDraft, setKeyDraft] = React.useState("");
  const [notesFocused, setNotesFocused] = React.useState(false);
  const [notesDraft, setNotesDraft] = React.useState("");
  const [durationFocused, setDurationFocused] = React.useState(false);
  const [durationDraft, setDurationDraft] = React.useState("");

  // Online status
  const [online, setOnline] = React.useState(isOnline());

  React.useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  // Metronome
  const metro = useMetronome();

  // Setlist
  const setlist = useSetlist();
  const [showSetlist, setShowSetlist] = React.useState(false);
  const [showBackup, setShowBackup] = React.useState(false);
  const [showNumpad, setShowNumpad] = React.useState(false);
  const [showRecordings, setShowRecordings] = React.useState(false);
  const [showPerf, setShowPerf] = React.useState(false);
  const [showKaraoke, setShowKaraoke] = React.useState(false);
  const [showGallery, setShowGallery] = React.useState(false);
  const [galleryUploading, setGalleryUploading] = React.useState(false);
  const photoInputRef = React.useRef(null);
  const videoInputRef = React.useRef(null);

  const uploadGalleryFile = React.useCallback(async (file) => {
    if (!file) return;
    setGalleryUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/gallery`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const isVid = (file.type || "").startsWith("video/");
      toast.success(isVid ? "Vidéo ajoutée à la galerie" : "Photo ajoutée à la galerie");
    } catch (e) {
      toast.error("Envoi impossible");
    } finally {
      setGalleryUploading(false);
    }
  }, []);

  const onPhotoPicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) uploadGalleryFile(f);
  };
  const onVideoPicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) uploadGalleryFile(f);
  };

  // Load songs
  const reloadSongs = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/songs`);
      setSongs(data);
    } catch (e) {
      toast.error("Impossible de charger les morceaux");
    }
  }, []);

  React.useEffect(() => {
    (async () => {
      await reloadSongs();
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, []);

  // Auto-select first song once loaded
  React.useEffect(() => {
    if (songs.length > 0 && !selectedId) {
      setSelectedId(songs[0].id);
    }
    // eslint-disable-next-line
  }, [songs.length]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return songs;
    return songs.filter(
      (s) =>
        s.id.toLowerCase().includes(q) ||
        s.title.toLowerCase().includes(q) ||
        (s.artist || "").toLowerCase().includes(q)
    );
  }, [query, songs]);

  // Probe MP3/WAV durations for the visible song list (Audio metadata only,
  // cached in localStorage by useAudioDurations). Used to show mm:ss next to
  // each song row, and to populate the setlist PDF / SUISA export.
  const songListAudioFilenames = React.useMemo(
    () =>
      filtered
        .map((s) => s.audio_playback_filename || s.audio_repete_filename || s.audio_filename)
        .filter(Boolean),
    [filtered]
  );
  const songListDurations = useAudioDurations(songListAudioFilenames);

  // Auto-select the (single) match when typing a code/title
  React.useEffect(() => {
    if (!query.trim()) return;
    if (filtered.length === 0) return;
    // Always promote the top match while searching
    if (filtered[0] && filtered[0].id !== selectedId) {
      setSelectedId(filtered[0].id);
      setPlaying(false);
    }
    // eslint-disable-next-line
  }, [query, filtered.length]);

  const selected = React.useMemo(
    () => songs.find((s) => s.id === selectedId) || null,
    [songs, selectedId]
  );

  // Sync metronome bpm + beat signature with selected song when changing songs
  React.useEffect(() => {
    if (selected) {
      metro.setBpm(selected.bpm || 100);
      metro.setBeat(selected.beat || "4/4");
    }
    // eslint-disable-next-line
  }, [selected?.id, selected?.beat]);

  // Audio handlers
  React.useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrentTime(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => {
      setPlaying(false);
      // Rehearsal mode: schedule auto-skip to next setlist song after pause
      const { mode, pause, nextId } = rehearsalRef.current;
      if (mode && nextId) {
        setPauseTargetId(nextId);
        setCountdown(pause);
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  React.useEffect(() => {
    if (audioRef.current) audioRef.current.loop = loop;
  }, [loop]);

  // Auto-open the PDF lyrics when the selected song changes — only when the user
  // has activated the sync toggle. We track the last song we auto-opened for so
  // closing the dialog doesn't immediately reopen it for the same song.
  React.useEffect(() => {
    if (!pdfSync) return;
    if (!selectedId) return;
    if (lastAutoOpenedRef.current === selectedId) return;
    lastAutoOpenedRef.current = selectedId;
    const song = songs.find((s) => s.id === selectedId);
    if (!song?.pdf_filename) {
      toast.warning(`Aucune partition pour ${song?.id || ""}`, {
        description: "Charge un PDF avec « Charger » pour ce morceau.",
        duration: 2500,
      });
      return;
    }
    // Stage-ready: open immediately, no defer. The iframe is already pre-mounted
    // (kept in the DOM with display:none when closed), so showing it is just a CSS toggle.
    setShowPdf(true);
  }, [selectedId, pdfSync, songs]);

  // Resolve audio source based on active variant.
  //
  // ⚠️ IMPORTANT: `activeAudioKind` MUST be derived from which file is really
  // chosen (playback/repete → files_audio bucket, live recording →
  // files_recording bucket), NOT from `active_variant` alone. Otherwise, if
  // the user clicks the "Live" tab while no recording exists, the fallback
  // picks the legacy `audio_filename` (audio bucket) but the URL asks the
  // recording bucket → 404 → "Lecture impossible".
  let activeAudioName = null;
  let activeAudioKind = "audio";
  if (selected) {
    if (selected.active_variant === "repete" && selected.audio_repete_filename) {
      activeAudioName = selected.audio_repete_filename;
      activeAudioKind = "audio";
    } else if (selected.active_variant === "live" && selected.active_recording_filename) {
      activeAudioName = selected.active_recording_filename;
      activeAudioKind = "recording";
    } else {
      // Playback (default) OR any fallback path: the file lives in the audio bucket.
      activeAudioName =
        selected.audio_playback_filename ||
        selected.audio_repete_filename ||
        selected.audio_filename ||
        null;
      activeAudioKind = "audio";
    }
  }
  const audioUrl = fileUrl(activeAudioKind, activeAudioName);
  const pdfUrl = selected ? fileUrl("pdf", selected.pdf_filename) : null;
  const coverUrl = selected ? fileUrl("cover", selected.cover_filename) : null;

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a || !audioUrl) {
      toast.message("Aucun audio chargé pour ce morceau", {
        description: "Utilise « Charger mes fichiers » pour ajouter un MP3.",
      });
      return;
    }
    if (a.paused) {
      try {
        await a.play();
        setPlaying(true);
      } catch (e) {
        // On mobile the track is often still loading when the user taps play, which
        // interrupts play() with an AbortError (a.error is null, networkState=LOADING).
        // Don't surface an error — mark intent and auto-start once the media is ready.
        if (!a.error && (e?.name === "AbortError" || a.readyState < 3)) {
          setPlaying(true);
          if (playWhenReadyRef.current) a.removeEventListener("canplay", playWhenReadyRef.current);
          const onReady = () => {
            a.removeEventListener("canplay", onReady);
            playWhenReadyRef.current = null;
            a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
          };
          playWhenReadyRef.current = onReady;
          a.addEventListener("canplay", onReady);
        } else {
          // TEMP-DIAGNOSTIC (remove once audio root cause is confirmed): surface the real
          // reason so we can tell network(2)/decode(3)/src-not-supported(4) vs autoplay block.
          const code = a.error ? a.error.code : "none";
          toast.error(`Lecture impossible — media code: ${code} · ${e?.name || e} · net:${a.networkState}`);
        }
      }
    } else {
      a.pause();
      setPlaying(false);
      if (playWhenReadyRef.current) {
        a.removeEventListener("canplay", playWhenReadyRef.current);
        playWhenReadyRef.current = null;
      }
    }
  };

  const seekFrac = (p) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    a.currentTime = p * duration;
  };

  const seekTo = (seconds) => {
    const a = audioRef.current;
    if (!a) return;
    const clamped = Math.max(0, Math.min(seconds, a.duration || seconds));
    a.currentTime = clamped;
  };

  const onUpdated = (updated) => {
    setSongs((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const onCreated = (created) => {
    setSongs((prev) => [...prev, created].sort((a, b) => a.id.localeCompare(b.id)));
    setSelectedId(created.id);
  };

  const updateBpm = async (next) => {
    if (!selected) return;
    const num = parseFloat(next);
    if (isNaN(num)) return;
    const clamped = Math.max(20, Math.min(300, num));
    setSongs((prev) => prev.map((s) => (s.id === selected.id ? { ...s, bpm: clamped } : s)));
    metro.setBpm(Math.round(clamped));
    try {
      await axios.patch(`${API}/songs/${selected.id}`, { bpm: clamped });
    } catch (e) {
      // silent
    }
  };

  const updateKey = async (next) => {
    if (!selected) return;
    const k = (next || "").trim();
    setSongs((prev) => prev.map((s) => (s.id === selected.id ? { ...s, key: k } : s)));
    try {
      await axios.patch(`${API}/songs/${selected.id}`, { key: k });
    } catch (e) {
      // silent
    }
  };

  const updateBeat = async (next) => {
    if (!selected) return;
    setSongs((prev) => prev.map((s) => (s.id === selected.id ? { ...s, beat: next } : s)));
    try {
      await axios.patch(`${API}/songs/${selected.id}`, { beat: next });
    } catch (_) { /* silent */ }
  };

  const updateNotes = async (next) => {
    if (!selected) return;
    const n = next || "";
    setSongs((prev) => prev.map((s) => (s.id === selected.id ? { ...s, notes: n } : s)));
    try {
      await axios.patch(`${API}/songs/${selected.id}`, { notes: n });
    } catch (e) {
      // silent
    }
  };

  const updateDuration = async (raw) => {
    if (!selected) return;
    const trimmed = (raw || "").trim();
    // Empty input clears the manual override (back to auto-detection)
    const parsed = trimmed === "" ? null : parseDurationInput(trimmed);
    if (trimmed !== "" && parsed === null) {
      toast.error("Format invalide. Utilise mm:ss (ex: 3:42)");
      return;
    }
    setSongs((prev) =>
      prev.map((s) => (s.id === selected.id ? { ...s, duration_seconds: parsed } : s))
    );
    try {
      await axios.patch(`${API}/songs/${selected.id}`, { duration_seconds: parsed });
    } catch (e) {
      // silent
    }
  };

  const switchVariant = async (variant) => {
    if (!selected) return;
    const fn = variant === "repete"
      ? selected.audio_repete_filename
      : variant === "live"
        ? selected.active_recording_filename
        : selected.audio_playback_filename;
    setSongs((prev) => prev.map((s) => (
      s.id === selected.id
        ? { ...s, active_variant: variant, audio_filename: variant === "live" ? s.audio_filename : (fn || s.audio_filename) }
        : s
    )));
    setPlaying(false);
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch {}
    }
    try {
      await axios.patch(`${API}/songs/${selected.id}`, { active_variant: variant });
    } catch (e) {
      // silent
    }
  };

  // Opens the phone's native voice recorder app (Samsung Voice Recorder /
  // Apple Voice Memos). Browsers can't guarantee a launch, so we try the
  // most reliable intent / URL scheme per platform and fall back to a
  // helpful toast when nothing catches.
  const openPhoneRecorder = () => {
    const ua = navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(ua);

    const fallback = () => {
      toast.message("Ouvre l'enregistreur vocal de ton téléphone pour enregistrer le Live.", {
        duration: 6000,
      });
    };

    if (isAndroid) {
      // Chrome Android intent URI. Samsung Voice Recorder ships as
      // com.sec.android.app.voicenote. If unavailable, the browser falls
      // back to the Play Store package (still lets the user pick another
      // recorder), or we show the toast if the intent throws.
      const intentUrl =
        "intent://record/#Intent;scheme=voicerecorder;" +
        "package=com.sec.android.app.voicenote;" +
        "S.browser_fallback_url=" +
        encodeURIComponent("https://play.google.com/store/apps/details?id=com.sec.android.app.voicenote") +
        ";end";
      try {
        window.location.href = intentUrl;
        // Some browsers silently fail; give the user a hint after a brief delay.
        window.setTimeout(fallback, 1500);
      } catch {
        fallback();
      }
      return;
    }

    if (isIOS) {
      // Apple's Voice Memos has no public URL scheme; iOS ≥ 13 respects
      // shortcuts but not from Safari without user setup. We show the
      // native prompt immediately.
      fallback();
      return;
    }

    fallback();
  };

  const goAdjacent = (delta) => {
    if (!selected || filtered.length === 0) return;
    const idx = filtered.findIndex((s) => s.id === selected.id);
    const base = idx < 0 ? 0 : idx;
    const next = (base + delta + filtered.length) % filtered.length;
    setSelectedId(filtered[next].id);
    setPlaying(false);
  };

  // Setlist helpers
  const setlistSongs = React.useMemo(
    () => setlist.setlist.song_ids.map((id) => songs.find((s) => s.id === id)).filter(Boolean),
    [setlist.setlist.song_ids, songs],
  );
  const setlistIndex = React.useMemo(
    () => (selected ? setlist.setlist.song_ids.indexOf(selected.id) : -1),
    [setlist.setlist.song_ids, selected?.id],
  );
  const nextInSetlist = React.useMemo(() => {
    if (setlistSongs.length === 0) return null;
    if (setlistIndex < 0) return setlistSongs[0];
    return setlistSongs[(setlistIndex + 1) % setlistSongs.length];
  }, [setlistSongs, setlistIndex]);

  const playNextInSetlist = () => {
    if (!nextInSetlist) {
      toast.message("Setlist vide", { description: "Ajoute des morceaux dans la setlist." });
      return;
    }
    setSelectedId(nextInSetlist.id);
    setPlaying(false);
  };

  const jumpToInSetlist = (songId) => {
    setSelectedId(songId);
    setPlaying(false);
    setShowSetlist(false);
  };

  // ----- Concert in-progress remaining-time tracker -----
  const setlistAudioFilenames = React.useMemo(
    () =>
      setlistSongs
        .map((s) => s.audio_playback_filename || s.audio_repete_filename || s.audio_filename)
        .filter(Boolean),
    [setlistSongs]
  );
  const setlistDurations = useAudioDurations(setlistAudioFilenames);

  const remainingSeconds = React.useMemo(() => {
    if (setlistSongs.length === 0) return 0;
    const startIdx = setlistIndex >= 0 ? setlistIndex : -1;
    let total = 0;
    // Current song: remaining = duration - currentTime.
    // Use resolveSongDuration so a manual override beats the probed value.
    if (startIdx >= 0) {
      const cur = setlistSongs[startIdx];
      const curDur = resolveSongDuration(cur, setlistDurations) || duration || 0;
      const left = Math.max(0, curDur - (currentTime || 0));
      total += left;
    }
    // Upcoming songs
    const upcoming = setlistSongs.slice(startIdx + 1);
    upcoming.forEach((s) => {
      total += resolveSongDuration(s, setlistDurations);
    });
    return total;
  }, [setlistSongs, setlistIndex, setlistDurations, currentTime, duration]);

  // ----- Threshold alert (5 min by default) -----
  const [alertThreshold, setAlertThreshold] = React.useState(() => {
    const v = parseInt(localStorage.getItem("gl26_remaining_threshold"), 10);
    return Number.isFinite(v) && v > 0 ? v : 300; // seconds
  });
  React.useEffect(() => {
    localStorage.setItem("gl26_remaining_threshold", String(alertThreshold));
  }, [alertThreshold]);

  const [vibrationOn, setVibrationOn] = React.useState(() => {
    const v = localStorage.getItem("gl26_vibration_on");
    return v === null ? true : v === "1";
  });
  React.useEffect(() => {
    localStorage.setItem("gl26_vibration_on", vibrationOn ? "1" : "0");
  }, [vibrationOn]);
  const vibrationSupported =
    typeof navigator !== "undefined" && typeof navigator.vibrate === "function";

  // ----- Rehearsal mode (auto-pause between songs) -----
  const [rehearsalMode, setRehearsalMode] = React.useState(() => {
    return localStorage.getItem("gl26_rehearsal_mode") === "1";
  });
  React.useEffect(() => {
    localStorage.setItem("gl26_rehearsal_mode", rehearsalMode ? "1" : "0");
  }, [rehearsalMode]);
  const [restPauseSec, setRestPauseSec] = React.useState(() => {
    const v = parseInt(localStorage.getItem("gl26_rest_pause_sec"), 10);
    return Number.isFinite(v) && v > 0 ? v : 60;
  });
  React.useEffect(() => {
    localStorage.setItem("gl26_rest_pause_sec", String(restPauseSec));
  }, [restPauseSec]);
  const [countdown, setCountdown] = React.useState(null);
  const [pauseTargetId, setPauseTargetId] = React.useState(null);
  const rehearsalRef = React.useRef({ mode: false, pause: 60, nextId: null });

  const [pauseTickOn, setPauseTickOn] = React.useState(() => {
    return localStorage.getItem("gl26_pause_tick_on") === "1";
  });
  React.useEffect(() => {
    localStorage.setItem("gl26_pause_tick_on", pauseTickOn ? "1" : "0");
  }, [pauseTickOn]);

  // Tempo of the song we're about to play during pause (so the player can mentally lock in)
  const pauseTargetSong = React.useMemo(
    () => (pauseTargetId ? songs.find((s) => s.id === pauseTargetId) : null),
    [pauseTargetId, songs]
  );
  useTempoTicker(
    pauseTargetSong?.bpm || 100,
    countdown !== null && pauseTickOn && !!pauseTargetSong
  );

  // Keep the latest rehearsal values inside a ref so the audio "ended" listener can read them
  React.useEffect(() => {
    rehearsalRef.current = {
      mode: rehearsalMode,
      pause: restPauseSec,
      // Only auto-advance if we're currently *on* a setlist song and the next exists
      nextId: setlistIndex >= 0 && nextInSetlist ? nextInSetlist.id : null,
    };
  }, [rehearsalMode, restPauseSec, setlistIndex, nextInSetlist]);

  // Countdown ticker
  React.useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown <= 0) {
      // Time's up -> advance and auto-play
      const target = pauseTargetId;
      setCountdown(null);
      setPauseTargetId(null);
      if (target) {
        setSelectedId(target);
        // Defer playing slightly to let <audio> swap its src
        setTimeout(() => setPlaying(true), 250);
      }
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown, pauseTargetId]);

  const cancelRehearsalPause = () => {
    setCountdown(null);
    setPauseTargetId(null);
  };
  const skipRehearsalPause = () => {
    const target = pauseTargetId;
    setCountdown(null);
    setPauseTargetId(null);
    if (target) {
      setSelectedId(target);
      setTimeout(() => setPlaying(true), 250);
    }
  };

  const belowThreshold =
    remainingSeconds > 0 && remainingSeconds <= alertThreshold;

  // Fire the toast once per "crossing" of the threshold
  const alertFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (belowThreshold && !alertFiredRef.current) {
      alertFiredRef.current = true;
      const mins = Math.ceil(remainingSeconds / 60);
      toast.warning(`Plus que ~${mins} min de concert`, {
        description: "Pense à préparer ta sortie de scène.",
        duration: 6000,
      });
      if (vibrationOn && vibrationSupported) {
        try { navigator.vibrate([200, 100, 200, 100, 400]); } catch (_) { /* ignore */ }
      }
    } else if (!belowThreshold && alertFiredRef.current && remainingSeconds > alertThreshold) {
      // Re-arm when we go back above the threshold (e.g., after a setlist change)
      alertFiredRef.current = false;
    }
  }, [belowThreshold, remainingSeconds, alertThreshold, vibrationOn, vibrationSupported]);

  // Reset arming whenever the active setlist changes
  React.useEffect(() => {
    alertFiredRef.current = false;
  }, [setlist.setlist?.id]);

  const progress = duration ? currentTime / duration : 0;

  return (
    <div className="min-h-screen w-full stage-glow text-white">
      {/* Visual Beat — silent stage metronome (hold ✓ 2s to toggle).
          Hidden while the PDF viewer is open so its big ✓ never overlaps the
          PDF's top-right Close button. */}
      {!showPdf && <VisualBeat bpm={selected?.bpm} beat={selected?.beat} />}
      {/* TOP HERO */}
      <header className="px-5 lg:px-10 pt-6 pb-4">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div>
              <Logo size="xl" />
              <div className="lg:hidden font-script text-2xl text-[var(--gl-gold)]/90 leading-tight mt-2">
                57 ans de scène, chaque seconde compte.
              </div>
            </div>
            <div
              className={`shrink-0 mt-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full border ${
                online
                  ? "border-[var(--gl-neon)]/50 text-[var(--gl-neon)] bg-[var(--gl-neon)]/5"
                  : "border-orange-500/60 text-orange-400 bg-orange-500/10"
              }`}
              data-testid="online-badge"
              title={online ? "Connecté" : "Hors ligne — lecture depuis le cache"}
            >
              {online ? <Wifi size={11} /> : <WifiOff size={11} />}
              {online ? "Online" : "Offline"}
            </div>
          </div>
          <div className="flex flex-col items-start lg:items-end gap-2">
            <div className="hidden lg:block max-w-sm">
              <div className="font-script text-3xl text-[var(--gl-gold)] leading-tight">
                57 ans de scène,<br />chaque seconde compte.
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowBackup(true)}
                className="flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-wider px-3 py-2 rounded-lg bg-[#0d0d0d] border border-white/15 hover:border-[var(--gl-neon)] text-white transition"
                data-testid="open-backup-btn"
                title="Sauvegarder ou restaurer toute l'app"
              >
                <Archive size={14} className="text-[var(--gl-neon)]" />
                Sauvegarde
              </button>
              <button
                onClick={() => setShowGallery(true)}
                className="flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-wider px-3 py-2 rounded-lg bg-[#0d0d0d] border border-white/15 hover:border-[var(--gl-gold)] text-white transition"
                data-testid="open-gallery-btn"
                title="Galerie du concert (photos & vidéos)"
              >
                <ImageIcon size={14} className="text-[var(--gl-gold)]" />
                Galerie
              </button>
              <button
                onClick={togglePdfSync}
                aria-pressed={pdfSync}
                className={`flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-wider px-3 py-2 rounded-lg border transition ${
                  pdfSync
                    ? "bg-[var(--gl-gold)] text-black border-[var(--gl-gold)]"
                    : "bg-[#0d0d0d] border-white/15 hover:border-[var(--gl-gold)] text-white"
                }`}
                data-testid="toggle-pdf-sync-btn"
                title={pdfSync
                  ? "Synchro PDF active — ouvre les paroles à chaque changement"
                  : "Activer la synchro PDF (ouvre les paroles auto)"}
              >
                <FileText size={14} className={pdfSync ? "text-black" : "text-[var(--gl-gold)]"} />
                Sync PDF
                {pdfSync && (
                  <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
                )}
              </button>
              <button
                onClick={() => setShowPerf(true)}
                disabled={!selected}
                className="flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-wider px-3 py-2 rounded-lg bg-[var(--gl-gold)] text-black hover:brightness-110 disabled:opacity-50 transition"
                data-testid="open-perf-btn"
                title="Mode Performance plein écran (scène)"
              >
                <Maximize2 size={14} />
                Mode Scène
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MAIN GRID */}
      <main className="px-3 sm:px-5 lg:px-10 safe-pb-player">
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-4 lg:gap-6">
          {/* LEFT FEATURES PANEL (hidden on mobile) */}
          <aside className="hidden lg:block col-span-3 space-y-3">
            <FeatureCard icon={Search} title="RECHERCHE RAPIDE" subtitle="Par code" sub2="A001 → A999" />
            <FeatureCard icon={Music} title="LECTURE IMMÉDIATE" subtitle="WAV / MP3" sub2="Haute qualité" />
            <FeatureCard icon={FileText} title="PAROLES PDF" subtitle="Affichage simultané" sub2="Synchronisé" />
            <FeatureCard icon={Activity} title="BPM VISIBLE" subtitle="Modifiable" sub2="En temps réel" />
            <FeatureCard icon={Piano} title="ACCORDS PIANO" subtitle="Affichés à la demande" />
            <FeatureCard icon={Upload} title="CHARGER MES FICHIERS" subtitle="Importez facilement" />
          </aside>

          {/* CENTER PLAYER */}
          <section className="col-span-12 lg:col-span-6 space-y-4">
            {/* Rehearsal pause banner */}
            {countdown !== null && (
              <div
                className="bg-[var(--gl-green)]/10 border-2 border-[var(--gl-green)]/60 rounded-2xl p-3 flex items-center gap-3 animate-pulse-soft"
                data-testid="rehearsal-pause-banner"
              >
                <CircularCountdown
                  value={countdown}
                  total={restPauseSec}
                  size={64}
                  stroke={5}
                  color="var(--gl-green)"
                  data-testid="rehearsal-countdown-ring"
                >
                  <div className="text-sm font-display text-white tabular-nums leading-none">
                    {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
                  </div>
                </CircularCountdown>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--gl-green)] font-bold">
                    Pause répétition
                  </div>
                  <div className="text-base text-white tabular-nums leading-tight">
                    {pauseTargetId && (
                      <span className="text-sm text-neutral-400 font-normal">
                        Suivant : <span className="text-[var(--gl-gold)] font-bold">{pauseTargetId}</span>
                        {pauseTargetSong?.bpm && (
                          <span className="text-neutral-500 ml-2 text-xs">
                            {Math.round(pauseTargetSong.bpm)} BPM
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPauseTickOn((v) => !v)}
                    className={`mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider rounded px-2 py-0.5 border transition-colors ${
                      pauseTickOn
                        ? "border-[var(--gl-gold)]/60 text-[var(--gl-gold)] bg-[var(--gl-gold)]/10"
                        : "border-white/15 text-neutral-400 hover:text-neutral-200"
                    }`}
                    data-testid="pause-tick-toggle"
                    aria-pressed={pauseTickOn}
                    title="Joue un tic du métronome au tempo du prochain morceau"
                  >
                    <Activity size={11} />
                    Tic {pauseTickOn ? "ON" : "OFF"}
                  </button>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button
                    onClick={skipRehearsalPause}
                    className="px-3 py-1.5 rounded-lg bg-[var(--gl-gold)] text-black text-xs font-bold uppercase tracking-wider hover:brightness-110"
                    data-testid="rehearsal-skip-btn"
                  >
                    Passer
                  </button>
                  <button
                    onClick={cancelRehearsalPause}
                    className="px-3 py-1.5 rounded-lg bg-black border border-white/15 text-neutral-300 text-xs font-bold uppercase tracking-wider hover:border-orange-400 hover:text-orange-400"
                    data-testid="rehearsal-cancel-btn"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Setlist banner */}
            {setlistSongs.length > 0 && (
              <div className="bg-[#0d0d0d] border border-[var(--gl-gold)]/40 rounded-2xl p-3 flex items-center gap-3" data-testid="setlist-banner">
                <div className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 transition-colors ${
                belowThreshold
                  ? "bg-orange-500/15 border-orange-500/60 text-orange-400 animate-pulse"
                  : "bg-[var(--gl-gold)]/10 border-[var(--gl-gold)]/30 text-[var(--gl-gold)]"
              }`} data-testid="setlist-banner-icon">
                <ListMusic size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold flex items-center gap-2 flex-wrap">
                  <span>{setlist.setlist.name} · {setlistIndex >= 0 ? `${setlistIndex + 1}/${setlistSongs.length}` : `0/${setlistSongs.length}`}</span>
                  {remainingSeconds > 0 && (
                    <span
                      className={`tabular-nums ${belowThreshold ? "text-orange-400" : "text-[var(--gl-green)]"}`}
                      data-testid="setlist-remaining-time"
                      title={setlistIndex >= 0 ? "Temps restant jusqu'à la fin du concert" : "Durée totale estimée"}
                    >
                      · Reste ≈ {formatDuration(remainingSeconds)}
                    </span>
                  )}
                  <select
                    value={alertThreshold}
                    onChange={(e) => setAlertThreshold(parseInt(e.target.value, 10))}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-black border border-white/15 rounded px-1.5 py-0.5 text-[10px] text-neutral-300 tracking-wider focus:outline-none focus:border-[var(--gl-gold)]"
                    data-testid="alert-threshold-select"
                    title="Seuil d'alerte de fin de concert"
                  >
                    <option value={180}>Alerte 3 min</option>
                    <option value={300}>Alerte 5 min</option>
                    <option value={600}>Alerte 10 min</option>
                  </select>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setRehearsalMode((v) => !v); }}
                    className={`rounded border px-1.5 py-0.5 flex items-center gap-1 text-[10px] tracking-wider transition-colors ${
                      rehearsalMode
                        ? "border-[var(--gl-gold)]/60 text-[var(--gl-gold)] bg-[var(--gl-gold)]/10 hover:border-[var(--gl-gold)]"
                        : "border-white/15 text-neutral-500 hover:text-neutral-300"
                    }`}
                    data-testid="rehearsal-mode-toggle"
                    title={rehearsalMode ? "Mode répétition ON · pause auto entre morceaux" : "Mode répétition OFF"}
                    aria-pressed={rehearsalMode}
                  >
                    <Timer size={12} />
                    <span className="hidden sm:inline">Répét.</span>
                  </button>
                  {rehearsalMode && (
                    <select
                      value={restPauseSec}
                      onChange={(e) => setRestPauseSec(parseInt(e.target.value, 10))}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-black border border-[var(--gl-gold)]/40 rounded px-1.5 py-0.5 text-[10px] text-[var(--gl-gold)] tracking-wider focus:outline-none"
                      data-testid="rest-pause-select"
                      title="Durée de la pause entre morceaux"
                    >
                      <option value={15}>Pause 15 s</option>
                      <option value={30}>Pause 30 s</option>
                      <option value={60}>Pause 1 min</option>
                      <option value={120}>Pause 2 min</option>
                    </select>
                  )}
                  {vibrationSupported && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const next = !vibrationOn;
                        setVibrationOn(next);
                        if (next) {
                          try { navigator.vibrate([80, 60, 80]); } catch (_) { /* ignore */ }
                        }
                      }}
                      className={`rounded border px-1.5 py-0.5 flex items-center gap-1 text-[10px] tracking-wider transition-colors ${
                        vibrationOn
                          ? "border-[var(--gl-green)]/50 text-[var(--gl-green)] hover:border-[var(--gl-green)]"
                          : "border-white/15 text-neutral-500 hover:text-neutral-300"
                      }`}
                      data-testid="vibration-toggle-btn"
                      title={vibrationOn ? "Vibration ON (toucher pour désactiver)" : "Vibration OFF (toucher pour activer)"}
                      aria-pressed={vibrationOn}
                    >
                      {vibrationOn ? <Vibrate size={12} /> : <VibrateOff size={12} />}
                      <span className="hidden sm:inline">{vibrationOn ? "Vib." : "Mute"}</span>
                    </button>
                  )}
                </div>
                {nextInSetlist && (
                  <div className="text-xs text-white truncate">
                    Suivant : <span className="text-[var(--gl-gold)] font-bold">{nextInSetlist.id}</span>{" "}
                    <span className="text-[var(--gl-gold)]">{nextInSetlist.title}</span>
                  </div>
                )}
              </div>
                <button
                  onClick={playNextInSetlist}
                  className="tap-lg px-4 rounded-lg bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider text-xs flex items-center gap-1.5 hover:brightness-110 active:scale-95 transition"
                  data-testid="setlist-next-btn"
                  title="Aller au morceau suivant de la setlist"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}

            {/* Search (sticky on mobile) */}
            <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl p-3 sticky top-2 z-30 backdrop-blur supports-[backdrop-filter]:bg-[#0d0d0d]/85 lg:static lg:bg-[#0d0d0d]">
              <div className="relative">
                <Search
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="A001, A002, A003…"
                  className="h-14 pl-12 pr-24 text-xl bg-black border-white/15 focus-visible:ring-[var(--gl-gold)] focus-visible:border-[var(--gl-gold)]"
                  data-testid="search-input"
                />
                <button
                  onClick={() => setShowNumpad(true)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-lg bg-black border border-[var(--gl-gold)]/40 hover:border-[var(--gl-gold)] text-[var(--gl-gold)] flex items-center justify-center transition"
                  data-testid="open-numpad-btn"
                  title="Pavé numérique"
                  aria-label="Ouvrir le pavé numérique"
                >
                  <Hash size={18} />
                </button>
                {query && (
                  <button
                    onClick={() => setQuery("")}
                    className="absolute right-14 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"
                    data-testid="search-clear"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>

            {/* Selected card */}
            {selected ? (
              <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl p-4 space-y-4" data-testid="selected-song-card">
                <div className="flex items-start gap-4">
                  <div className="font-display text-4xl leading-none text-white shrink-0 flex items-center gap-3" data-testid="selected-song-id">
                    {selected.id}
                    <CompletenessRing song={selected} size={28} showLabel />
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div
                      className="text-[var(--gl-gold)] font-display text-2xl leading-tight truncate"
                      data-testid="selected-song-title"
                    >
                      {selected.title || "—"}
                    </div>
                    <div
                      className="text-[var(--gl-green)] font-semibold text-base leading-tight truncate"
                      data-testid="selected-song-composer"
                    >
                      {selected.composer || selected.artist || "Compositeur ?"}
                    </div>
                  </div>
                </div>

                {/* Missing fields hint */}
                {(() => {
                  const c = computeCompleteness(selected);
                  if (c.done >= c.total) return null;
                  return (
                    <button
                      onClick={() => setShowUpload(true)}
                      className="w-full text-left rounded-xl bg-[#121212] border border-[var(--gl-gold)]/30 hover:border-[var(--gl-gold)]/60 px-3 py-2 flex items-center justify-between gap-2 transition"
                      data-testid="completeness-hint"
                    >
                      <div className="text-[11px] text-neutral-400 truncate">
                        <span className="text-[var(--gl-gold)] font-bold tabular-nums">{c.done}/{c.total}</span>{" "}
                        <span className="uppercase tracking-widest">Manque :</span>{" "}
                        <span className="text-white">{c.missing.map((m) => m.label).join(" · ")}</span>
                      </div>
                      <span className="text-[10px] uppercase tracking-widest text-[var(--gl-gold)] font-bold shrink-0">Compléter →</span>
                    </button>
                  );
                })()}

                {/* Waveform + time */}
                <div className="flex items-center gap-3">
                  <span className="text-xs text-neutral-400 w-10 tabular-nums">{fmt(currentTime)}</span>
                  <Waveform seed={selected.id} progress={progress} onSeek={seekFrac} count={typeof window !== "undefined" && window.innerWidth < 640 ? 40 : 56} />
                  <span className="text-xs text-neutral-400 w-10 tabular-nums text-right">{fmt(duration)}</span>
                </div>

                {/* Transport */}
                <div className="flex items-center justify-center gap-6 pt-1">
                  <button
                    onClick={() => goAdjacent(-1)}
                    className="w-14 h-14 rounded-full bg-[#1a1a1a] border border-white/10 flex items-center justify-center hover:bg-white/5 transition"
                    aria-label="Précédent"
                    data-testid="prev-btn"
                  >
                    <SkipBack size={22} />
                  </button>
                  <button
                    onClick={togglePlay}
                    className="w-20 h-20 rounded-full bg-[var(--gl-gold)] text-black flex items-center justify-center shadow-[0_0_30px_-8px_rgba(245,183,26,0.7)] hover:brightness-110 active:scale-95 transition"
                    aria-label={playing ? "Pause" : "Play"}
                    data-testid="play-btn"
                  >
                    {playing ? <Pause size={30} /> : <Play size={30} className="ml-1" />}
                  </button>
                  <button
                    onClick={() => goAdjacent(1)}
                    className="w-14 h-14 rounded-full bg-[#1a1a1a] border border-white/10 flex items-center justify-center hover:bg-white/5 transition"
                    aria-label="Suivant"
                    data-testid="next-btn"
                  >
                    <SkipForward size={22} />
                  </button>
                </div>

                {/* Stage notes (inline edit) */}
                <div className="bg-[#0d0d0d] border border-white/10 rounded-xl px-4 py-3" data-testid="notes-block">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold flex items-center gap-2">
                    Notes de scène
                  </div>
                  <textarea
                    value={notesFocused ? notesDraft : (selected.notes || "")}
                    onFocus={() => { setNotesDraft(selected.notes || ""); setNotesFocused(true); }}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    onBlur={() => {
                      setNotesFocused(false);
                      if (notesDraft !== (selected.notes || "")) updateNotes(notesDraft);
                    }}
                    rows={selected.notes || notesFocused ? 3 : 1}
                    placeholder="Tap pour ajouter une note (intro guitare, 2e couplet a cappella…)"
                    className="mt-1 w-full bg-transparent border-0 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:ring-0 resize-none leading-snug"
                    data-testid="notes-textarea"
                    spellCheck={false}
                  />
                </div>

                {/* BPM + BEAT + Tonalité + Durée (edit as text) */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="meta-row">
                  <div className="bg-black rounded-xl border border-white/10 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">BPM</div>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={bpmFocused ? bpmDraft : fmtBpm(selected.bpm)}
                      onFocus={() => {
                        setBpmDraft(selected.bpm != null ? String(selected.bpm) : "");
                        setBpmFocused(true);
                      }}
                      onChange={(e) => setBpmDraft(e.target.value)}
                      onBlur={() => {
                        setBpmFocused(false);
                        const n = parseFloat(bpmDraft.replace(",", "."));
                        if (!isNaN(n)) updateBpm(n);
                      }}
                      placeholder="000.000"
                      className="w-full bg-transparent border-0 text-[var(--gl-gold)] font-display text-2xl sm:text-3xl tabular-nums focus:outline-none focus:ring-0 p-0 mt-1"
                      data-testid="bpm-value"
                      aria-label="BPM"
                    />
                  </div>
                  <div className="bg-black rounded-xl border border-white/10 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Beat</div>
                    <select
                      value={selected.beat || "4/4"}
                      onChange={(e) => updateBeat(e.target.value)}
                      className="w-full bg-transparent border-0 text-[var(--gl-gold)] font-display text-2xl sm:text-3xl tabular-nums focus:outline-none focus:ring-0 p-0 mt-1 appearance-none cursor-pointer"
                      data-testid="beat-value"
                      aria-label="Beat (signature rythmique)"
                    >
                      {["2/4", "3/4", "4/4", "6/8", "12/8"].map((b) => (
                        <option key={b} value={b} className="bg-black">{b}</option>
                      ))}
                    </select>
                  </div>
                  <div className="bg-black rounded-xl border border-white/10 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold">Tonalité</div>
                    <input
                      type="text"
                      value={keyFocused ? keyDraft : (selected.key || "")}
                      onFocus={() => { setKeyDraft(selected.key || ""); setKeyFocused(true); }}
                      onChange={(e) => setKeyDraft(e.target.value)}
                      onBlur={() => {
                        setKeyFocused(false);
                        if (keyDraft !== (selected.key || "")) updateKey(keyDraft);
                      }}
                      placeholder="Ex: Sol, La m…"
                      className="w-full bg-transparent border-0 text-[var(--gl-gold)] font-display text-2xl sm:text-3xl placeholder:text-neutral-700 focus:outline-none focus:ring-0 p-0 mt-1"
                      data-testid="key-value"
                      aria-label="Tonalité"
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                  <div
                    className="bg-black rounded-xl border border-white/10 px-4 py-3 col-span-2 sm:col-span-1"
                    title={
                      selected.duration_seconds && selected.duration_seconds > 0
                        ? "Durée manuelle (prioritaire). Laisse vide pour utiliser la durée auto-détectée."
                        : "Durée manuelle. Si vide, la durée est lue depuis le MP3."
                    }
                  >
                    <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-bold flex items-center gap-1.5">
                      Durée
                      {selected.duration_seconds && selected.duration_seconds > 0 ? (
                        <span className="text-[var(--gl-gold)] text-[8px]">manuelle</span>
                      ) : (
                        <span className="text-neutral-600 text-[8px]">auto</span>
                      )}
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={
                        durationFocused
                          ? durationDraft
                          : selected.duration_seconds && selected.duration_seconds > 0
                            ? formatDuration(selected.duration_seconds)
                            : ""
                      }
                      onFocus={() => {
                        setDurationDraft(
                          selected.duration_seconds && selected.duration_seconds > 0
                            ? formatDuration(selected.duration_seconds)
                            : ""
                        );
                        setDurationFocused(true);
                      }}
                      onChange={(e) => setDurationDraft(e.target.value)}
                      onBlur={() => {
                        setDurationFocused(false);
                        const cur = selected.duration_seconds && selected.duration_seconds > 0
                          ? formatDuration(selected.duration_seconds)
                          : "";
                        if (durationDraft.trim() !== cur) updateDuration(durationDraft);
                      }}
                      placeholder="mm:ss"
                      className="w-full bg-transparent border-0 text-[var(--gl-gold)] font-display text-2xl sm:text-3xl tabular-nums placeholder:text-neutral-700 focus:outline-none focus:ring-0 p-0 mt-1"
                      data-testid="duration-value"
                      aria-label="Durée du morceau"
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </div>
                </div>

                {/* Tap-tempo */}
                <TapTempoButton onBpm={(b) => updateBpm(b)} />

                {/* Variant selector: REPETE / PLAYBACK / LIVE */}
                <div className="bg-black rounded-xl border border-white/10 p-1.5 flex items-center gap-1" data-testid="variant-switcher">
                  <button
                    onClick={() => switchVariant("repete")}
                    className={`flex-1 tap-lg rounded-lg font-bold uppercase tracking-wider text-[10px] sm:text-xs transition flex items-center justify-center gap-1 ${
                      selected.active_variant === "repete"
                        ? "bg-[var(--gl-gold)] text-black"
                        : "text-white/70 hover:text-white"
                    }`}
                    data-testid="variant-repete-btn"
                  >
                    <Music size={12} /> Répéte
                    {selected.audio_repete_filename ? (
                      <span className="text-[9px] opacity-70">●</span>
                    ) : (
                      <span className="text-[9px] opacity-50">·</span>
                    )}
                  </button>
                  <button
                    onClick={() => switchVariant("playback")}
                    className={`flex-1 tap-lg rounded-lg font-bold uppercase tracking-wider text-[10px] sm:text-xs transition flex items-center justify-center gap-1 ${
                      selected.active_variant === "playback" || !selected.active_variant
                        ? "bg-[var(--gl-gold)] text-black"
                        : "text-white/70 hover:text-white"
                    }`}
                    data-testid="variant-playback-btn"
                  >
                    <Music size={12} /> Playback
                    {selected.audio_playback_filename ? (
                      <span className="text-[9px] opacity-70">●</span>
                    ) : (
                      <span className="text-[9px] opacity-50">·</span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (selected.active_variant === "live") {
                        if ((selected.recordings || []).length > 0) setShowRecordings(true);
                      } else {
                        switchVariant("live");
                      }
                    }}
                    className={`flex-1 tap-lg rounded-lg font-bold uppercase tracking-wider text-[10px] sm:text-xs transition flex items-center justify-center gap-1 ${
                      selected.active_variant === "live"
                        ? "bg-red-500 text-black"
                        : "text-white/70 hover:text-white"
                    }`}
                    data-testid="variant-live-btn"
                    title={selected.active_variant === "live" ? "Voir les enregistrements" : "Bascule sur Live"}
                  >
                    <Mic size={12} /> Live
                    {(selected.recordings || []).length > 0 ? (
                      <span className="text-[9px] opacity-80">{(selected.recordings || []).length}</span>
                    ) : (
                      <span className="text-[9px] opacity-50">·</span>
                    )}
                  </button>
                </div>

                {/* Phone-recorder shortcut — opens the OS's native voice
                    recorder (Samsung Voice Recorder on Android, Voice Memos
                    hint on iOS). G!GLife26 does not capture nor store any
                    audio for this flow; the user records with their phone. */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={openPhoneRecorder}
                    className="flex-1 tap-lg rounded-xl font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 transition border-2 bg-[#121212] border-red-500/40 text-red-400 hover:border-red-500 hover:text-red-300"
                    style={{ minHeight: 52 }}
                    data-testid="open-phone-recorder-btn"
                    title="Ouvrir l'enregistreur vocal du téléphone"
                  >
                    <Mic size={16} />
                    Enregistreur téléphone
                  </button>
                  {(selected.recordings || []).length > 0 && (
                    <button
                      onClick={() => setShowRecordings(true)}
                      className="tap-lg rounded-xl bg-[#121212] border border-white/10 hover:border-[var(--gl-gold)]/60 px-3 text-xs font-bold uppercase tracking-wider"
                      style={{ minHeight: 52 }}
                      data-testid="open-recordings-btn"
                      title="Voir les enregistrements importés"
                    >
                      {(selected.recordings || []).length}
                    </button>
                  )}
                </div>


                {/* Action row */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setShowChords(true)}
                    className="tap-lg rounded-xl bg-[#121212] border border-white/10 hover:border-[var(--gl-gold)]/60 transition flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-2 text-xs sm:text-sm font-bold uppercase tracking-wider"
                    data-testid="chords-btn"
                    title="Analyse automatique des accords depuis le Playback"
                  >
                    <Piano size={18} className="text-[var(--gl-gold)]" />
                    <span>Accords</span>
                    {selected.chords_data?.items?.length > 0 && (
                      <span
                        className="ml-1 px-1.5 py-0.5 rounded text-[9px] tabular-nums bg-[var(--gl-gold)] text-black"
                        title="Grille disponible"
                      >
                        {selected.chords_data.items.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setLoop((v) => !v)}
                    className={`tap-lg rounded-xl border transition flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-2 text-xs sm:text-sm font-bold uppercase tracking-wider ${
                      loop
                        ? "bg-[var(--gl-gold)] text-black border-[var(--gl-gold)]"
                        : "bg-[#121212] border-white/10 hover:border-[var(--gl-gold)]/60"
                    }`}
                    data-testid="loop-btn"
                    aria-pressed={loop}
                  >
                    <Repeat size={18} className={loop ? "" : "text-[var(--gl-gold)]"} />
                    <span>Loop</span>
                  </button>
                  <button
                    onClick={metro.toggle}
                    className={`tap-lg rounded-xl border transition flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-2 text-xs sm:text-sm font-bold uppercase tracking-wider ${
                      metro.on
                        ? "bg-[var(--gl-gold)] text-black border-[var(--gl-gold)]"
                        : "bg-[#121212] border-white/10 hover:border-[var(--gl-gold)]/60"
                    }`}
                    data-testid="metronome-btn"
                    aria-pressed={metro.on}
                  >
                    <Activity size={18} className={metro.on ? "" : "text-[var(--gl-gold)]"} />
                    <span>Métronome</span>
                  </button>
                </div>

                {/* Photo/Video buttons.
                    Pattern: <label> visible (styled as button) containing a "visually
                    hidden" <input type="file" capture>. The native browser behaviour
                    forwards the label's click to its child input — no JS click()
                    needed, so it works on Samsung Internet / MIUI Browser / older
                    Android Chrome / iOS Safari without quirks. */}
                <div className="grid grid-cols-4 gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowPdf(true)}
                    className="rounded-lg bg-white text-black font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1.5 hover:brightness-95 transition px-1"
                    style={{ minHeight: 36 }}
                    data-testid="view-lyrics-btn"
                  >
                    <FileText size={14} /> Paroles
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowUpload(true)}
                    className="rounded-lg bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1.5 hover:brightness-110 transition px-1"
                    style={{ minHeight: 36 }}
                    data-testid="upload-btn"
                  >
                    <Upload size={14} /> Charger
                  </button>
                  <label
                    htmlFor="gl26-photo-input"
                    className={`rounded-lg bg-[#0d0d0d] border border-[var(--gl-gold)]/50 text-[var(--gl-gold)] font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1.5 transition px-1 select-none ${galleryUploading ? "opacity-50 pointer-events-none" : "cursor-pointer active:bg-[var(--gl-gold)] active:text-black hover:bg-[var(--gl-gold)] hover:text-black"}`}
                    style={{ minHeight: 36 }}
                    data-testid="capture-photo-btn"
                    title="Prendre une photo"
                  >
                    <Camera size={14} /> Photo
                    <input
                      ref={photoInputRef}
                      id="gl26-photo-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={onPhotoPicked}
                      data-testid="gallery-photo-input"
                      tabIndex={-1}
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        padding: 0,
                        margin: -1,
                        overflow: "hidden",
                        clip: "rect(0 0 0 0)",
                        whiteSpace: "nowrap",
                        border: 0,
                      }}
                    />
                  </label>
                  <label
                    htmlFor="gl26-video-input"
                    className={`rounded-lg bg-[#0d0d0d] border border-[var(--gl-neon)]/50 text-[var(--gl-neon)] font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1.5 transition px-1 select-none ${galleryUploading ? "opacity-50 pointer-events-none" : "cursor-pointer active:bg-[var(--gl-neon)] active:text-black hover:bg-[var(--gl-neon)] hover:text-black"}`}
                    style={{ minHeight: 36 }}
                    data-testid="capture-video-btn"
                    title="Filmer une vidéo"
                  >
                    <Video size={14} /> Vidéo
                    <input
                      ref={videoInputRef}
                      id="gl26-video-input"
                      type="file"
                      accept="video/*"
                      capture="environment"
                      onChange={onVideoPicked}
                      data-testid="gallery-video-input"
                      tabIndex={-1}
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        padding: 0,
                        margin: -1,
                        overflow: "hidden",
                        clip: "rect(0 0 0 0)",
                        whiteSpace: "nowrap",
                        border: 0,
                      }}
                    />
                  </label>
                </div>
                {/* Transcription Live button — pro stage teleprompter (replaces karaoke mode) */}
                <button
                  type="button"
                  onClick={() => setShowKaraoke(true)}
                  className="w-full rounded-xl border border-[var(--gl-neon)]/40 bg-[#0d0d0d] text-[var(--gl-neon)] hover:bg-[var(--gl-neon)] hover:text-black font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 py-2.5 transition"
                  data-testid="open-karaoke-btn"
                  title="Transcription Live — téléprompteur de scène synchronisé avec l'audio"
                >
                  <Sparkles size={14} />
                  Transcription Live
                  {selected.karaoke_lrc && (
                    <span
                      className="ml-1 px-1.5 py-0.5 rounded text-[9px] tabular-nums bg-[var(--gl-neon)] text-black"
                      title="Synchro disponible"
                    >
                      {Math.round((selected.karaoke_speed || 1) * 100)}%
                    </span>
                  )}
                </button>
              </div>
            ) : (
              <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl p-6 text-center text-neutral-400">
                {loading ? "Chargement…" : "Aucun morceau sélectionné"}
              </div>
            )}
          </section>

          {/* RIGHT LIST */}
          <aside className="col-span-12 lg:col-span-3">
            <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl p-3">
              <div className="flex items-center justify-between px-2 py-2 gap-2">
                <div className="text-xs uppercase tracking-widest text-neutral-400">
                  {filtered.length} morceau{filtered.length > 1 ? "x" : ""}
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setShowSetlist(true)}
                    className="h-9 px-2.5 rounded-full bg-black border border-[var(--gl-gold)]/40 hover:border-[var(--gl-gold)] flex items-center gap-1.5 text-xs font-bold text-[var(--gl-gold)] uppercase tracking-wider transition"
                    data-testid="open-setlist-btn"
                  >
                    <ListMusic size={14} />
                    Setlist
                    {setlistSongs.length > 0 && (
                      <span className="ml-0.5 inline-block min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--gl-gold)] text-black text-[10px] font-bold leading-[18px] text-center">
                        {setlistSongs.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setShowNewSong(true)}
                    className="w-9 h-9 rounded-full bg-[var(--gl-gold)] text-black flex items-center justify-center hover:brightness-110"
                    aria-label="Ajouter un morceau"
                    data-testid="add-song-btn"
                  >
                    <PlusIcon size={18} />
                  </button>
                </div>
              </div>
              <ul className="space-y-2 lg:max-h-[60vh] lg:overflow-y-auto no-scrollbar" data-testid="song-list">
                {filtered.map((s) => {
                  const active = s.id === selectedId;
                  const inSetlist = setlist.setlist.song_ids.includes(s.id);
                  const setlistPos = setlist.setlist.song_ids.indexOf(s.id);
                  const songAudio = s.audio_playback_filename || s.audio_repete_filename || s.audio_filename;
                  const resolvedDur = resolveSongDuration(s, songListDurations);
                  // Priority: manual override > auto-detected > probing state > none
                  const isManual = s.duration_seconds && s.duration_seconds > 0;
                  const probedDur = songAudio ? songListDurations[songAudio] : null;
                  const durLabel = isManual
                    ? formatDuration(resolvedDur)
                    : resolvedDur > 0
                      ? formatDuration(resolvedDur)
                      : songAudio && probedDur === undefined
                        ? "·:··"
                        : "--:--";
                  return (
                    <li key={s.id} className="relative">
                      <button
                        onClick={() => { setSelectedId(s.id); setPlaying(false); }}
                        className={`w-full text-left rounded-xl p-4 lg:p-3 border transition flex items-center gap-3 tap-lg ${
                          active
                            ? "bg-white/5 border-[var(--gl-gold)]/60"
                            : "bg-black border-white/10 hover:border-white/30"
                        }`}
                        data-testid={`song-item-${s.id}`}
                      >
                        <div className="font-display text-xl lg:text-lg text-white flex items-center gap-2">
                          {s.id}
                          <CompletenessRing song={s} size={16} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[var(--gl-gold)] font-bold truncate text-base lg:text-sm leading-tight">{s.title}</div>
                          {(s.composer || s.artist) && (
                            <div className="text-[var(--gl-green)] truncate text-xs leading-tight">{s.composer || s.artist}</div>
                          )}
                          <div
                            className="text-[10px] text-neutral-400 tabular-nums leading-tight mt-0.5"
                            data-testid={`song-meta-${s.id}`}
                          >
                            {s.bpm ? `${Math.round(s.bpm)} BPM` : "— BPM"} · {s.beat || "4/4"}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span
                            className={`text-[11px] font-bold tabular-nums rounded px-1.5 py-0.5 border ${
                              isManual
                                ? "text-[var(--gl-gold)] bg-[var(--gl-gold)]/15 border-[var(--gl-gold)]/40"
                                : "text-white/70 bg-white/5 border-white/10"
                            }`}
                            title={
                              isManual
                                ? `Durée manuelle ${durLabel}`
                                : songAudio
                                  ? `Durée auto ${durLabel}`
                                  : "Aucun audio"
                            }
                            data-testid={`song-duration-${s.id}`}
                          >
                            {durLabel}
                          </span>
                          {inSetlist && (
                            <span className="text-[10px] font-bold text-[var(--gl-gold)] bg-[var(--gl-gold)]/15 border border-[var(--gl-gold)]/50 rounded px-1.5 py-0.5 tabular-nums">
                              #{setlistPos + 1}
                            </span>
                          )}
                          {s.key && (
                            <span className="text-[10px] font-bold text-[var(--gl-gold)] bg-[var(--gl-gold)]/10 border border-[var(--gl-gold)]/40 rounded px-1.5 py-0.5 tabular-nums">
                              {s.key}
                            </span>
                          )}
                          {(s.audio_playback_filename || s.audio_repete_filename || s.audio_filename) && <Music size={16} className="text-[var(--gl-gold)]" />}
                          {s.pdf_filename && <FileText size={16} className="text-white/70" />}
                          {s.notes && <StickyNote size={16} className="text-[var(--gl-neon)]" />}
                        </div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (inSetlist) setlist.removeSong(s.id);
                          else setlist.addSong(s.id);
                        }}
                        className={`absolute -right-1 -top-1 w-7 h-7 rounded-full flex items-center justify-center border-2 transition ${
                          inSetlist
                            ? "bg-[var(--gl-gold)] text-black border-black"
                            : "bg-black text-white/60 border-white/10 hover:border-[var(--gl-gold)] hover:text-[var(--gl-gold)]"
                        }`}
                        title={inSetlist ? "Retirer de la setlist" : "Ajouter à la setlist"}
                        data-testid={`toggle-setlist-${s.id}`}
                      >
                        {inSetlist ? <X size={14} /> : <PlusIcon size={14} />}
                      </button>
                    </li>
                  );
                })}
                {filtered.length === 0 && (
                  <li className="text-center text-neutral-500 text-sm py-6">Aucun résultat</li>
                )}
              </ul>
            </div>
          </aside>
        </div>

        {/* BOTTOM TRUST BAR */}
        <div className="max-w-7xl mx-auto mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex items-center gap-3 bg-[#0a0a0a] border border-white/10 rounded-xl p-3">
            <Zap size={20} className="text-[var(--gl-gold)]" />
            <div>
              <div className="text-sm font-bold">ULTRA RAPIDE</div>
              <div className="text-xs text-neutral-400">Conçu pour la scène</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-[#0a0a0a] border border-white/10 rounded-xl p-3">
            <ShieldCheck size={20} className="text-[var(--gl-gold)]" />
            <div>
              <div className="text-sm font-bold">FIABLE</div>
              <div className="text-xs text-neutral-400">Lecture stable</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-[#0a0a0a] border border-white/10 rounded-xl p-3">
            <Heart size={20} className="text-[var(--gl-gold)]" />
            <div>
              <div className="text-sm font-bold">FAIT POUR LES MUSICIENS</div>
              <div className="text-xs text-neutral-400">Par un musicien, pour les musiciens</div>
            </div>
          </div>
        </div>
      </main>

      {/* Persistent mini bottom player (visible during PDF too) */}
      {selected && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur border-t border-white/10 safe-bottom"
          data-testid="bottom-player"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-white font-bold text-sm shrink-0">{selected.id}</span>
                <span className="text-[var(--gl-gold)] font-bold text-sm truncate">{selected.title}</span>
                {(selected.composer || selected.artist) && (
                  <span className="text-[var(--gl-green)] text-sm truncate hidden sm:inline">
                    · {selected.composer || selected.artist}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-neutral-500 tabular-nums">
                {fmt(currentTime)} / {fmt(duration)}{loop ? " · LOOP" : ""}{metro.on ? " · METRO" : ""} · {selected.active_variant === "repete" ? "RÉP" : "PB"}
              </div>
            </div>
            <button
              onClick={togglePlay}
              className="w-12 h-12 rounded-full bg-[var(--gl-gold)] text-black flex items-center justify-center hover:brightness-110 active:scale-95 transition shrink-0"
              aria-label={playing ? "Pause" : "Play"}
              data-testid="bottom-play-btn"
            >
              {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
            </button>
          </div>
        </div>
      )}

      {/* Hidden audio element (kept mounted for continuous playback) */}
      <audio
        ref={audioRef}
        src={audioUrl || undefined}
        preload="metadata"
        data-testid="audio-element"
      />

      {/* PDF overlay (audio keeps playing).
          Always mounted so the iframe is pre-loaded for the current song;
          tapping "Paroles" becomes a CSS visibility toggle, not a network fetch. */}
      <PdfViewer
        open={showPdf}
        url={pdfUrl}
        songId={selected?.id}
        onClose={() => setShowPdf(false)}
        onPrev={() => {
          // Use the active setlist order if not empty, else full alphabetical song list.
          const list = setlistSongs.length > 0 ? setlistSongs : songs;
          const i = list.findIndex((s) => s.id === selected?.id);
          if (i > 0) setSelectedId(list[i - 1].id);
        }}
        onNext={() => {
          const list = setlistSongs.length > 0 ? setlistSongs : songs;
          const i = list.findIndex((s) => s.id === selected?.id);
          if (i >= 0 && i < list.length - 1) setSelectedId(list[i + 1].id);
        }}
        hasPrev={(() => {
          const list = setlistSongs.length > 0 ? setlistSongs : songs;
          const i = list.findIndex((s) => s.id === selected?.id);
          return i > 0;
        })()}
        hasNext={(() => {
          const list = setlistSongs.length > 0 ? setlistSongs : songs;
          const i = list.findIndex((s) => s.id === selected?.id);
          return i >= 0 && i < list.length - 1;
        })()}
        prevId={(() => {
          const list = setlistSongs.length > 0 ? setlistSongs : songs;
          const i = list.findIndex((s) => s.id === selected?.id);
          return i > 0 ? list[i - 1].id : null;
        })()}
        nextId={(() => {
          const list = setlistSongs.length > 0 ? setlistSongs : songs;
          const i = list.findIndex((s) => s.id === selected?.id);
          return i >= 0 && i < list.length - 1 ? list[i + 1].id : null;
        })()}
      />

      <ChordsDialog
        open={showChords}
        onOpenChange={setShowChords}
        song={selected}
        axiosInstance={axios}
        onSaved={reloadSongs}
      />
      <UploadDialog
        open={showUpload}
        onOpenChange={setShowUpload}
        song={selected}
        onUpdated={onUpdated}
      />
      <NewSongDialog
        open={showNewSong}
        onOpenChange={setShowNewSong}
        onCreated={onCreated}
        existingIds={songs.map((s) => s.id)}
      />
          <SetlistDialog
        open={showSetlist}
        onOpenChange={setShowSetlist}
        setlist={setlist.setlist}
        setlists={setlist.setlists}
        activeId={setlist.activeId}
        songs={songs}
        onAdd={setlist.addSong}
        onRemove={setlist.removeSong}
        onMoveUp={setlist.moveUp}
        onMoveDown={setlist.moveDown}
        onReorder={setlist.setSongIds}
        onRename={setlist.setName}
        onClear={setlist.clear}
        onJump={jumpToInSetlist}
        onCreateSetlist={setlist.createSetlist}
        onDeleteSetlist={setlist.deleteSetlist}
        onDuplicateSetlist={setlist.duplicateSetlist}
        onActivateSetlist={setlist.activateSetlist}
        onRenameSetlist={setlist.renameSetlist}
      />
      <BackupDialog
        open={showBackup}
        onOpenChange={setShowBackup}
        onImported={async () => {
          await reloadSongs();
          await setlist.reload();
        }}
      />
      <GalleryDialog
        open={showGallery}
        onOpenChange={setShowGallery}
      />
      <KaraokeDialog
        open={showKaraoke}
        onOpenChange={setShowKaraoke}
        song={selected}
        audioPlaying={playing}
        audioCurrentTime={currentTime}
        audioDuration={duration}
        audioReady={duration > 0}
        onTogglePlay={togglePlay}
        onSeek={seekTo}
        onSongUpdated={(updated) => {
          setSongs((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        }}
      />
      <RecordingsDialog
        open={showRecordings}
        onOpenChange={setShowRecordings}
        song={selected}
        onUpdated={(data) => setSongs((prev) => prev.map((s) => (s.id === data.id ? data : s)))}
      />
      <NumpadDialog
        open={showNumpad}
        onClose={() => setShowNumpad(false)}
        songs={songs}
        onSelect={(id) => { setSelectedId(id); setPlaying(false); }}
      />
      <PerformanceMode
        open={showPerf}
        onClose={() => setShowPerf(false)}
        selected={selected}
        playing={playing}
        currentTime={currentTime}
        duration={duration}
        onTogglePlay={togglePlay}
        onPrev={() => goAdjacent(-1)}
        onNext={() => goAdjacent(1)}
        onSeekFrac={seekFrac}
        onShowLyrics={() => setShowPdf(true)}
        nextInSetlist={nextInSetlist}
        onNextInSetlist={playNextInSetlist}
        hasPdf={!!pdfUrl}
        onSwitchVariant={switchVariant}
        hasRepete={!!selected?.audio_repete_filename}
        hasPlayback={!!(selected?.audio_playback_filename || selected?.audio_filename)}
        hasLive={!!selected?.active_recording_filename}
      />
    </div>
  );
}
